import type { VoiceoverDto, VoiceoverIssueDto } from '@app/shared';
import type { Dictionary } from '../i18n';

/** «0:07» — a recording timer. */
export function formatClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** «пропущено «без»» — what the engine heard in a rejected own take. */
export function voiceIssueText(issue: VoiceoverIssueDto, t: Dictionary): string {
  const say = t.voiceover.issue;
  if (issue.type === 'missing') return say.missing(issue.expected ?? '…');
  if (issue.type === 'extra') return say.extra(issue.heard ?? '…');
  return say.substituted(issue.expected ?? '…', issue.heard ?? '…');
}

/** Only our own paths: a `?back=` parameter must not send the person anywhere else. */
export function safeBackPath(raw: string | null, fallback = '/settings'): string {
  return raw && raw.startsWith('/') && !raw.startsWith('//') && !raw.includes('\\')
    ? raw
    : fallback;
}

/** false on a phone or browser that cannot record in the app: the chat is the way then (spec 6.3). */
export function canRecordAudio(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

/**
 * The places of a take worth a listen (2026-10-05: the person decides, not the check): the
 * misheard words of a minor check — an extra word alone is the recognizer's — or every
 * difference of a gross one. A clean take has none.
 */
export function checkIssues(take: Pick<VoiceoverDto, 'check' | 'issues'>): VoiceoverIssueDto[] {
  if (take.check === 'clean') return [];
  return take.check === 'gross' ? take.issues : take.issues.filter((i) => i.type !== 'extra');
}

/** The first differences of a rejected own take; the rest is a count, not a wall of words. */
export function shortIssues<T>(issues: readonly T[], max = 3): { shown: T[]; more: number } {
  return { shown: issues.slice(0, max), more: Math.max(0, issues.length - max) };
}
